import type { SecodaThemeShades } from '@repo/theme/types';
import type { SelectablePropertyItem } from '../SingleSelector/types';
import type { IMultiSelectorTargetProps } from './MultiSelectorTarget';

/**
 * Transforms tag data into SelectablePropertyItem format for use with MultiSelectorTarget.
 *
 * @param tags - Array of tag objects with id, name, and color
 * @returns Array of SelectablePropertyItem objects
 */
export function transformTagsToSelectableItems(
	tags: Array<{ id: string; name: string; color?: SecodaThemeShades }>
): SelectablePropertyItem[] {
	return tags.map((tag) => ({
		label: tag.name,
		value: tag.id,
		color: tag.color,
	}));
}

/**
 * Transforms user/owner data into SelectablePropertyItem format for use with MultiSelectorTarget.
 * Handles both user objects and user groups.
 *
 * @param owners - Array of user objects with display_name, profile_picture, etc.
 * @param groups - Array of user group objects with name and icon
 * @returns Array of SelectablePropertyItem objects
 */
export function transformOwnersToSelectableItems(
	owners: Array<{
		id: string;
		display_name?: string;
		first_name?: string;
		last_name?: string;
		profile_picture?: string | null;
	}> = [],
	groups: Array<{ id: string; name: string; icon?: string }> = []
): SelectablePropertyItem[] {
	const userItems: SelectablePropertyItem[] = owners.map((owner) => ({
		label:
			owner.display_name || `${owner.first_name} ${owner.last_name}`.trim(),
		value: owner.id,
		icon: owner.profile_picture || null,
	}));

	const groupItems: SelectablePropertyItem[] = groups.map((group) => ({
		label: group.name,
		value: group.id,
		icon: group.icon || null,
		navigateTo: `/group/${group.id}`,
	}));

	return [...userItems, ...groupItems];
}

/**
 * Creates the common props for MultiSelectorTarget component.
 * This abstracts the shared logic between read-only and interactive modes.
 *
 * @param props - The base props for MultiSelectorTarget
 * @param options - Additional options for customization
 * @returns Props object for MultiSelectorTarget
 */
export function createMultiSelectorTargetProps(
	props: Omit<IMultiSelectorTargetProps, 'variant'>,
	options: {
		variant?: IMultiSelectorTargetProps['variant'];
	} = {}
) {
	const { variant = 'tertiary' } = options;

	const baseProps: IMultiSelectorTargetProps = {
		hideOnEmpty: props.hideOnEmpty,
		placeholder: props.placeholder,
		placeholderIconName: props.placeholderIconName,
		inheritSelected: props.inheritSelected,
		selected: props.selected,
		property: props.property,
		propertyLabel: props.propertyLabel,
		iconType: props.iconType,
		isMenuItemBadge: props.isMenuItemBadge,
		isViewerUser: props.isViewerUser,
		permittedId: props.permittedId,
		readOnly: props.readOnly,
		w: props.w,
		className: props.className,
		withDescriptionTooltip: props.withDescriptionTooltip,
		onTargetClick: props.onTargetClick,
		isChromeUser: props.isChromeUser,
		truncate: props.truncate,
		variant,
	};

	return baseProps;
}
